import React, { useMemo } from 'react';
import { diffLines } from 'diff';
import { Box, Typography } from '@mui/material';

/* ================================================================== */
/*  JSON Diff Viewer  (diffchecker-style side-by-side)                 */
/* ================================================================== */

export default function JsonDiffViewer({
  oldConfig,
  newConfig,
  title = null,
  oldLabel = 'Old Config',
  newLabel = 'Repair Config',
  mode = 'json',
}) {
  const { leftLines, rightLines } = useMemo(() => {
    const oldStr = mode === 'text' ? String(oldConfig ?? '') : JSON.stringify(oldConfig, null, 2);
    const newStr = mode === 'text' ? String(newConfig ?? '') : JSON.stringify(newConfig, null, 2);
    const parts = diffLines(oldStr, newStr);

    const left = [];   // { text, type, lineNo }
    const right = [];  // type: 'same' | 'removed' | 'added' | 'empty'
    let leftNum = 1;
    let rightNum = 1;

    parts.forEach((part) => {
      const lines = part.value.replace(/\n$/, '').split('\n');
      if (!part.added && !part.removed) {
        lines.forEach((l) => {
          left.push({ text: l, type: 'same', lineNo: leftNum++ });
          right.push({ text: l, type: 'same', lineNo: rightNum++ });
        });
      } else if (part.removed) {
        lines.forEach((l) => {
          left.push({ text: l, type: 'removed', lineNo: leftNum++ });
          right.push({ text: '', type: 'empty', lineNo: null });
        });
      } else if (part.added) {
        let fillIdx = right.length - lines.length;
        let canFill = fillIdx >= 0;
        if (canFill) {
          for (let k = fillIdx; k < right.length; k++) {
            if (right[k].type !== 'empty') { canFill = false; break; }
          }
        }
        if (canFill && lines.length <= (right.length - fillIdx)) {
          lines.forEach((l, idx) => {
            right[fillIdx + idx] = { text: l, type: 'added', lineNo: rightNum++ };
          });
        } else {
          lines.forEach((l) => {
            left.push({ text: '', type: 'empty', lineNo: null });
            right.push({ text: l, type: 'added', lineNo: rightNum++ });
          });
        }
      }
    });

    return { leftLines: left, rightLines: right };
  }, [oldConfig, newConfig, mode]);

  const lineStyle = (type) => {
    switch (type) {
      case 'removed':
        return { background: 'rgba(239, 68, 68, 0.13)', color: '#fca5a5' };
      case 'added':
        return { background: 'rgba(16, 185, 129, 0.13)', color: '#6ee7b7' };
      case 'empty':
        return { background: 'rgba(148, 163, 184, 0.04)', color: 'transparent' };
      default:
        return { background: 'transparent', color: '#94a3b8' };
    }
  };

  const gutterStyle = (type) => ({
    width: 44,
    minWidth: 44,
    textAlign: 'right',
    pr: 1,
    pl: 0.5,
    userSelect: 'none',
    fontSize: '0.72rem',
    color: type === 'empty' ? 'transparent' : 'rgba(148,163,184,0.45)',
    borderRight: '1px solid rgba(148,163,184,0.1)',
    background: type === 'removed'
      ? 'rgba(239,68,68,0.08)'
      : type === 'added'
        ? 'rgba(16,185,129,0.08)'
        : 'transparent',
  });

  const prefixChar = (type) => {
    if (type === 'removed') return '−';
    if (type === 'added') return '+';
    return ' ';
  };

  const renderSide = (lines, label, labelColor) => (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{
        px: 1.5, py: 0.75,
        background: labelColor === 'red' ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
        borderBottom: '1px solid rgba(148,163,184,0.1)',
        display: 'flex', alignItems: 'center', gap: 1,
        flexShrink: 0,
      }}>
        <Box sx={{
          width: 8, height: 8, borderRadius: '50%',
          background: labelColor === 'red' ? '#ef4444' : '#10b981',
        }} />
        <Typography sx={{
          fontWeight: 700, fontSize: '0.78rem', letterSpacing: 0.5,
          color: labelColor === 'red' ? '#fca5a5' : '#6ee7b7',
        }}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ overflow: mode === 'text' ? 'hidden' : 'auto', flex: 1 }}>
        <Box sx={{ minWidth: mode === 'text' ? '100%' : 'fit-content' }}>
          {lines.map((line, i) => {
            const style = lineStyle(line.type);
            return (
              <Box key={i} sx={{ display: 'flex', minHeight: 24, lineHeight: '24px', background: style.background }}>
                <Box sx={{
                  ...gutterStyle(line.type),
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  background: line.type === 'removed'
                    ? '#1a1520'
                    : line.type === 'added'
                      ? '#141d1a'
                      : '#0f172a',
                }}>
                  {line.lineNo}
                </Box>
                <Box sx={{
                  width: 20, minWidth: 20, textAlign: 'center',
                  fontWeight: 700, fontSize: '0.78rem',
                  color: line.type === 'removed' ? '#ef4444' : line.type === 'added' ? '#10b981' : 'rgba(148,163,184,0.2)',
                }}>
                  {prefixChar(line.type)}
                </Box>
                <Box sx={{
                  fontFamily: mode === 'text' ? 'inherit' : '"JetBrains Mono", "Fira Code", "SF Mono", Consolas, monospace',
                  fontSize: mode === 'text' ? '0.85rem' : '0.8rem',
                  whiteSpace: mode === 'text' ? 'pre-wrap' : 'pre',
                  wordBreak: mode === 'text' ? 'break-word' : 'normal',
                  lineHeight: mode === 'text' ? 1.5 : '24px',
                  py: mode === 'text' ? 0.25 : 0,
                  px: 1,
                  pr: 3,
                  flex: mode === 'text' ? 1 : undefined,
                  minWidth: 0,
                  color: style.color,
                }}>
                  {line.text || ' '}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {title && (
        <Typography sx={{
          color: '#cbd5e1', fontSize: '0.82rem', fontWeight: 600, mb: 0.75,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{
        display: 'flex',
        border: '1px solid rgba(148,163,184,0.15)',
        borderRadius: 1.5,
        overflow: 'hidden',
        width: '100%',
        background: 'rgba(15, 23, 42, 0.6)',
      }}>
        {renderSide(leftLines, oldLabel, 'red')}
        <Box sx={{ width: '1px', background: 'rgba(148,163,184,0.15)', flexShrink: 0 }} />
        {renderSide(rightLines, newLabel, 'green')}
      </Box>
    </Box>
  );
}
